import { useEffect, useRef, useState } from "react";
import { VolumeX } from "lucide-react";
import { useLang } from "../i18n";

export function AudioPlayer() {
  const { t } = useLang();
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(0.35);

  useEffect(() => {
    const a = new Audio("/assets/ambient.mp3");
    a.loop = true;
    a.preload = "auto";
    a.volume = 0.35;
    ref.current = a;

    let unlocked = false;
    const tryPlay = () => {
      if (unlocked) return;
      a.play()
        .then(() => {
          unlocked = true;
          setPlaying(true);
          window.removeEventListener("pointerdown", tryPlay);
          window.removeEventListener("keydown", tryPlay);
          window.removeEventListener("scroll", tryPlay);
        })
        .catch(() => {});
    };
    tryPlay();
    window.addEventListener("pointerdown", tryPlay);
    window.addEventListener("keydown", tryPlay);
    window.addEventListener("scroll", tryPlay, { passive: true });
    return () => {
      window.removeEventListener("pointerdown", tryPlay);
      window.removeEventListener("keydown", tryPlay);
      window.removeEventListener("scroll", tryPlay);
      a.pause();
    };
  }, []);

  const toggle = async () => {
    const a = ref.current;
    if (!a) return;
    if (playing) {
      a.pause();
      setPlaying(false);
      return;
    }
    try {
      await a.play();
      setPlaying(true);
    } catch {
      setPlaying(false);
    }
  };

  const changeVolume = (e) => {
    const v = Number(e.target.value);
    setVolume(v);
    if (ref.current) ref.current.volume = v;
  };

  return (
    <div className="fixed bottom-6 left-6 z-[150] flex items-center gap-3" data-testid="audio-player">
      <button
        data-testid="audio-toggle-button"
        onClick={toggle}
        aria-label={playing ? t.audio.off : t.audio.on}
        title={playing ? t.audio.off : t.audio.on}
        className="glass flex h-12 w-12 items-center justify-center rounded-full border-[#C9A227]/30 text-[#C9A227] hover:bg-[#C9A227]/15 transition-[background-color] duration-300"
      >
        {playing ? (
          <span className="flex h-4 items-end gap-[3px]" aria-hidden="true">
            <span className="eq-bar" />
            <span className="eq-bar" />
            <span className="eq-bar" />
          </span>
        ) : (
          <VolumeX size={18} />
        )}
      </button>
      <input
        data-testid="audio-volume-slider"
        type="range"
        min="0"
        max="1"
        step="0.05"
        value={volume}
        onChange={changeVolume}
        aria-label={t.audio.label}
        className="mx-volume hidden sm:block w-24"
      />
    </div>
  );
}
